import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatProfilePictureUrl } from '../utils/profilePictureHelper';
import {
  PageHeader,
  SearchBar,
  LoadingSpinner,
  EmptyState,
  ListContainer,
  Pagination,
  Badge,
  ErrorMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button
} from '../components/common';

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/Users', {
        params: {
          pageNumber: currentPage,
          pageSize: pageSize,
          searchString: searchString || undefined
        }
      });
      setUsers(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  };

  const getUserRoles = (user) => {
    const roles = [];
    if (user.isAdmin) roles.push({ label: 'Admin', variant: 'danger' });
    if (user.isManager) roles.push({ label: 'Manager', variant: 'primary' });
    if (user.isOwner) roles.push({ label: 'Owner', variant: 'accent' });
    if (user.isResident) roles.push({ label: 'Resident', variant: 'secondary' });
    if (user.isStaff) roles.push({ label: 'Staff', variant: 'outline' });

    return roles;
  };

  if (loading) {
    return <LoadingSpinner size="medium" />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Users" />
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        action={{
          label: "Add New User",
          href: "/users/create"
        }}
      />

      <SearchBar
        placeholder="Search by name, email, or phone..."
        value={searchString}
        onChange={setSearchString}
        onSearch={handleSearch}
        className="mb-6"
      />

      <ListContainer>
        {loading ? (
          <div className="flex justify-center p-12">
            <LoadingSpinner size="medium" />
          </div>
        ) : users.length === 0 ? (
          <EmptyState message="No users found." icon="👥" />
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>User</TableCell>
                  <TableCell header>Contact</TableCell>
                  <TableCell header>Unit</TableCell>
                  <TableCell header>Roles</TableCell>
                  <TableCell header>
                    <span className="sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.userId} clickable onClick={() => navigate(`/users/${user.userId}`)}>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {user.profilePictureUrl ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover transition-transform hover:scale-105"
                              src={formatProfilePictureUrl(user.profilePictureUrl)}
                              alt={`${user.firstName} ${user.lastName}`}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center transition-transform hover:scale-105">
                              <span className="text-sm font-medium text-primary-700">
                                {getInitials(user.firstName, user.lastName)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                            {`${user.firstName} ${user.lastName}`}
                          </div>
                          <div className="text-sm text-slate-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700">
                        {user.mobilePhone || <span className="text-slate-400 italic">No phone</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700 font-medium">
                        {user.unitNumber ? `Unit ${user.unitNumber}${user.floor ? ` - Fl ${user.floor}` : ''}` : <span className="text-slate-400 italic">None</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {getUserRoles(user).map((role, index) => (
                          <Badge
                            key={index}
                            variant={role.variant}
                            size="small"
                          >
                            {role.label}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end pr-4">
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/users/${user.userId}`);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-4 flex justify-between items-center px-2">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </ListContainer>
    </div>
  );
};

export default Users;